import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { UserService } from '../../services';
import type { VerifyUpdateRequest } from '../../services/types';
import { handleNucleusResponse } from '../../services/utils';
import type {
  UpdateUserPreferencesRequest,
  UpdateUserRequest,
} from '@billx/types';
import { getTokenManager } from '../../config/tokenManager';

import { useUserAuth } from '../auth/useUserAuth';
import type { QueryOptions } from '../types';

export const useCurrentUser = (options?: QueryOptions) => {
  const { authUser } = useUserAuth();
  const queryClient = useQueryClient();

  const QUERY_KEY = ['currentUser', authUser?.id];
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      if (!authUser?.id) {
        throw new Error('No authenticated user');
      }
      const response = await UserService.fetchCurrentUser();
      return handleNucleusResponse(response).data;
    },
    enabled: !!authUser?.id && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval,
    initialData: authUser || undefined,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const updateUser = useMutation({
    mutationFn: (data: UpdateUserRequest) => {
      if (!authUser?.id) {
        throw new Error('No authenticated user');
      }
      return UserService.updateUser(authUser.id, data);
    },
    onSuccess: (response: any) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, () => response.data);
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const updateUserPreference = useMutation({
    mutationFn: (preferences: UpdateUserPreferencesRequest) => {
      if (!authUser?.id) {
        throw new Error('No authenticated user');
      }
      return UserService.updateUserPreference(authUser.id, preferences);
    },
    onSuccess: (response: any) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, () => response.data);
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const verifyUpdate = useMutation({
    mutationFn: (data: VerifyUpdateRequest) => {
      if (!authUser?.id) {
        throw new Error('No authenticated user');
      }
      return UserService.verifyUpdate(authUser.id, data);
    },
    onSuccess: (response: any) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, () => response.data);
        getTokenManager().showNotification({
          message: 'Verification successful',
          severity: 'success',
        });
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const { data: currentUser, ...restQuery } = query;
  return {
    // Query data
    currentUser,
    ...restQuery,
    // Mutations
    updateUser: updateUser,
    updateUserPreference: updateUserPreference,
    verifyUpdate: verifyUpdate,
  };
};
